'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import ConsultationTypeCard from './ConsultationTypeCard';
import InvestmentLevelCard from './InvestmentLevelCard';
import ProgressIndicator from './ProgressIndicator';
import FormInput from './FormInput';
import FormTextarea from './FormTextarea';
import FormSelect from './FormSelect';
import CalendarSlot from './CalendarSlot';
import BenefitCard from './BenefitCard';
import { useLanguage } from '@/contexts/LanguageContext';

interface FormData {
  consultationType: string;
  investmentLevel: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  investmentGoals: string;
  riskTolerance: string;
  investmentExperience: string;
  preferredDate: string;
  preferredTime: string;
  additionalNotes: string;
  timezone: string;
}

interface FormErrors {
  [key: string]: string;
}

export default function GetStartedInteractive() {
  const [isHydrated, setIsHydrated] = useState(false);
  const { t } = useLanguage();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<FormData>({
    consultationType: '',
    investmentLevel: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    investmentGoals: '',
    riskTolerance: '',
    investmentExperience: '',
    preferredDate: '',
    preferredTime: '',
    additionalNotes: '',
    timezone: '',
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    if (typeof window !== 'undefined') {
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      setFormData((prev) => ({ ...prev, timezone: detectedTimezone }));
    }
  }, []);

  const steps = [t('getStarted.type'), t('getStarted.level'), t('getStarted.details'), t('getStarted.schedule'), t('getStarted.review')];

  const consultationTypes = [
    {
      icon: '💼',
      title: t('getStarted.initialConsultation'),
      description: t('getStarted.initialConsultationDesc'),
      duration: `30 ${t('getStarted.minutes')}`,
      value: 'initial',
    },
    {
      icon: '📊',
      title: t('getStarted.portfolioReview'),
      description: t('getStarted.portfolioReviewDesc'),
      duration: `45 ${t('getStarted.minutes')}`,
      value: 'portfolio',
    },
    {
      icon: '🎯',
      title: t('getStarted.strategySession'),
      description: t('getStarted.strategySessionDesc'),
      duration: `60 ${t('getStarted.minutes')}`,
      value: 'strategy',
    },
    {
      icon: '🤝',
      title: t('getStarted.partnershipDiscussion'),
      description: t('getStarted.partnershipDiscussionDesc'),
      duration: `45 ${t('getStarted.minutes')}`,
      value: 'partnership',
    },
  ];

  const investmentLevels = [
    {
      title: t('getStarted.emergingInvestor'),
      range: '$500K - $1M',
      features: [
        'Quarterly performance reviews',
        'Access to core investment strategies',
        'Email support and updates',
        'Annual strategy consultation',
      ],
      value: 'emerging',
    },
    {
      title: t('getStarted.establishedInvestor'),
      range: '$1M - $3M',
      features: [
        'Monthly performance reviews',
        'Priority access to new opportunities',
        'Dedicated advisor support',
        'Quarterly strategy sessions',
      ],
      value: 'established',
    },
    {
      title: t('getStarted.premiumInvestor'),
      range: '$3M - $5M',
      features: [
        'Weekly performance updates',
        'Exclusive deal flow access',
        'Direct team communication',
        'Monthly strategy consultations',
      ],
      value: 'premium',
    },
    {
      title: t('getStarted.elitePartnership'),
      range: '$5M+',
      features: [
        'Real-time performance tracking',
        'First access to all opportunities',
        'Personal relationship manager',
        'Custom strategy development',
      ],
      value: 'elite',
    },
  ];

  const benefits = [
    {
      icon: 'ShieldCheckIcon',
      title: t('getStarted.transparentPerformance'),
      description: t('getStarted.transparentPerformanceDesc'),
    },
    {
      icon: 'ChartBarIcon',
      title: t('getStarted.provenTrackRecord'),
      description: t('getStarted.provenTrackRecordDesc'),
    },
    {
      icon: 'UserGroupIcon',
      title: t('getStarted.expertTeam'),
      description: t('getStarted.expertTeamDesc'),
    },
    {
      icon: 'LockClosedIcon',
      title: t('getStarted.secureCompliant'),
      description: t('getStarted.secureCompliantDesc'),
    },
  ];

  const availableSlots = [
    { date: 'Jan 20, 2026', time: '10:00 AM', value: '2026-01-20T10:00' },
    { date: 'Jan 20, 2026', time: '2:00 PM', value: '2026-01-20T14:00' },
    { date: 'Jan 21, 2026', time: '11:00 AM', value: '2026-01-21T11:00' },
    { date: 'Jan 21, 2026', time: '3:00 PM', value: '2026-01-21T15:00' },
    { date: 'Jan 22, 2026', time: '9:00 AM', value: '2026-01-22T09:00' },
    { date: 'Jan 22, 2026', time: '1:00 PM', value: '2026-01-22T13:00' },
  ];

  const riskToleranceOptions = [
    { value: 'conservative', label: 'Conservative - Preserve capital with minimal risk' },
    { value: 'moderate', label: 'Moderate - Balance growth and stability' },
    { value: 'aggressive', label: 'Aggressive - Maximize growth potential' },
  ];

  const experienceOptions = [
    { value: 'beginner', label: 'Beginner - New to investing' },
    { value: 'intermediate', label: 'Intermediate - Some investment experience' },
    { value: 'advanced', label: 'Advanced - Experienced investor' },
    { value: 'professional', label: 'Professional - Financial industry background' },
  ];

  const validateStep = (step: number): boolean => {
    const errors: FormErrors = {};

    if (step === 1 && !formData.consultationType) {
      errors.consultationType = 'Please select a consultation type';
    }

    if (step === 2 && !formData.investmentLevel) {
      errors.investmentLevel = 'Please select an investment level';
    }

    if (step === 3) {
      if (!formData.firstName.trim()) errors.firstName = 'First name is required';
      if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
      if (!formData.email.trim()) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Please enter a valid email';
      }
      if (!formData.phone.trim()) errors.phone = 'Phone number is required';
      if (!formData.investmentGoals.trim()) errors.investmentGoals = 'Investment goals are required';
      if (!formData.riskTolerance) errors.riskTolerance = 'Risk tolerance is required';
      if (!formData.investmentExperience) errors.investmentExperience = 'Investment experience is required';
    }

    if (step === 4) {
      if (!formData.preferredDate) errors.preferredDate = 'Please select a date and time';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, 5));
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    setFormErrors({});
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) return;

    setIsSubmitting(true);
    await new Promise((resolve) => setTimeout(resolve, 2000));
    setIsSubmitting(false);
    setShowSuccess(true);
  };

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-3/4 mx-auto" />
            <div className="h-6 bg-muted rounded w-1/2 mx-auto" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-48 bg-muted rounded-lg" />
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-card rounded-2xl shadow-lg p-12 border border-border">
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Icon name="CheckCircleIcon" size={48} variant="solid" className="text-success" />
            </div>
            <h2 className="text-3xl font-heading font-bold text-foreground mb-4">
              Consultation Scheduled!
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Thank you for choosing Inversión Rápida. We've sent a confirmation email to{' '}
              <span className="font-semibold text-accent">{formData.email}</span> with your
              consultation details and preparation materials.
            </p>
            <div className="bg-muted/50 rounded-lg p-6 mb-8 text-left">
              <h3 className="font-heading font-semibold text-foreground mb-4">
                What happens next:
              </h3>
              <ul className="space-y-3">
                <li className="flex items-start space-x-3">
                  <Icon name="EnvelopeIcon" size={20} variant="outline" className="text-accent mt-0.5" />
                  <span className="text-sm text-muted-foreground">
                    Check your email for calendar invite and preparation checklist
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <Icon name="DocumentTextIcon" size={20} variant="outline" className="text-accent mt-0.5" />
                  <span className="text-sm text-muted-foreground">
                    Review the personalized materials we've sent based on your profile
                  </span>
                </li>
                <li className="flex items-start space-x-3">
                  <Icon name="VideoCameraIcon" size={20} variant="outline" className="text-accent mt-0.5" />
                  <span className="text-sm text-muted-foreground">
                    Join the video call at your scheduled time (link in email)
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground mb-4">
            Start Your Investment Journey
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Schedule a personalized consultation with our investment experts. We'll help you
            understand our strategies and find the perfect fit for your financial goals.
          </p>
        </div>

        <div className="bg-card rounded-2xl shadow-lg p-8 md:p-12 border border-border mb-12">
          <ProgressIndicator currentStep={currentStep} totalSteps={5} steps={steps} />

          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                  Choose Your Consultation Type
                </h2>
                <p className="text-muted-foreground">
                  Select the consultation that best matches your needs
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {consultationTypes.map((type) => (
                  <ConsultationTypeCard
                    key={type.value}
                    icon={type.icon}
                    title={type.title}
                    description={type.description}
                    duration={type.duration}
                    isSelected={formData.consultationType === type.value}
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, consultationType: type.value }))
                    }
                  />
                ))}
              </div>
              {formErrors.consultationType && (
                <p className="text-error text-sm text-center">{formErrors.consultationType}</p>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                  Select Your Investment Level
                </h2>
                <p className="text-muted-foreground">
                  Choose the tier that aligns with your investment capacity
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {investmentLevels.map((level) => (
                  <InvestmentLevelCard
                    key={level.value}
                    title={level.title}
                    range={level.range}
                    features={level.features}
                    isSelected={formData.investmentLevel === level.value}
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, investmentLevel: level.value }))
                    }
                  />
                ))}
              </div>
              {formErrors.investmentLevel && (
                <p className="text-error text-sm text-center">{formErrors.investmentLevel}</p>
              )}
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                  {t('form.tellUsAboutYourself')}
                </h2>
                <p className="text-muted-foreground">
                  {t('form.helpUsPrepare')}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormInput
                  label={t('form.firstName')}
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  placeholder={t('form.placeholderFirstName')}
                  required
                  error={formErrors.firstName}
                />
                <FormInput
                  label={t('form.lastName')}
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder={t('form.placeholderLastName')}
                  required
                  error={formErrors.lastName}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormInput
                  label={t('form.emailAddress')}
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t('form.placeholderEmail')}
                  required
                  error={formErrors.email}
                />
                <FormInput
                  label={t('form.phoneNumber')}
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={t('form.placeholderPhone')}
                  required
                  error={formErrors.phone}
                />
              </div>
              <FormTextarea
                label={t('form.investmentGoals')}
                name="investmentGoals"
                value={formData.investmentGoals}
                onChange={handleInputChange}
                placeholder={t('form.placeholderInvestmentGoals')}
                required
                rows={4}
                error={formErrors.investmentGoals}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormSelect
                  label={t('form.riskTolerance')}
                  name="riskTolerance"
                  value={formData.riskTolerance}
                  onChange={handleInputChange}
                  options={riskToleranceOptions}
                  required
                  error={formErrors.riskTolerance}
                />
                <FormSelect
                  label={t('form.investmentExperience')}
                  name="investmentExperience"
                  value={formData.investmentExperience}
                  onChange={handleInputChange}
                  options={experienceOptions}
                  required
                  error={formErrors.investmentExperience}
                />
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                  {t('form.scheduleYourConsultation')}
                </h2>
                <p className="text-muted-foreground">
                  {t('form.selectPreferredDateTime')} ({formData.timezone})
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {availableSlots.map((slot) => (
                  <CalendarSlot
                    key={slot.value}
                    date={slot.date}
                    time={slot.time}
                    isSelected={formData.preferredDate === slot.value}
                    onClick={() =>
                      setFormData((prev) => ({ ...prev, preferredDate: slot.value }))
                    }
                  />
                ))}
              </div>
              {formErrors.preferredDate && (
                <p className="text-error text-sm text-center">{formErrors.preferredDate}</p>
              )}
              <FormTextarea
                label={t('form.additionalNotes')}
                name="additionalNotes"
                value={formData.additionalNotes}
                onChange={handleInputChange}
                placeholder={t('form.placeholderAdditionalNotes')}
                rows={3}
              />
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground mb-2">
                  {t('form.reviewYourDetails')}
                </h2>
                <p className="text-muted-foreground">
                  {t('form.confirmInformation')}
                </p>
              </div>
              <div className="bg-muted/50 rounded-lg p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('form.consultationType')}</p>
                    <p className="font-medium text-foreground">
                      {
                        consultationTypes.find((t) => t.value === formData.consultationType)
                          ?.title
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">{t('form.investmentLevel')}</p>
                    <p className="font-medium text-foreground">
                      {
                        investmentLevels.find((l) => l.value === formData.investmentLevel)
                          ?.title
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Name</p>
                    <p className="font-medium text-foreground">
                      {formData.firstName} {formData.lastName}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Email</p>
                    <p className="font-medium text-foreground">{formData.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Phone</p>
                    <p className="font-medium text-foreground">{formData.phone}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Scheduled Time</p>
                    <p className="font-medium text-foreground">
                      {
                        availableSlots.find((s) => s.value === formData.preferredDate)?.date
                      }{' '}
                      at{' '}
                      {
                        availableSlots.find((s) => s.value === formData.preferredDate)?.time
                      }
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Investment Goals</p>
                  <p className="text-sm text-foreground">{formData.investmentGoals}</p>
                </div>
                {formData.additionalNotes && (
                  <div>
                    <p className="text-sm text-muted-foreground mb-1">Additional Notes</p>
                    <p className="text-sm text-foreground">{formData.additionalNotes}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-8 border-t border-border">
            {currentStep > 1 && (
              <button
                onClick={handleBack}
                className="flex items-center space-x-2 px-6 py-3 rounded-lg border border-border text-foreground hover:bg-muted transition-all duration-300"
              >
                <Icon name="ArrowLeftIcon" size={20} variant="outline" />
                <span className="font-medium">Back</span>
              </button>
            )}
            {currentStep < 5 ? (
              <button
                onClick={handleNext}
                className="ml-auto flex items-center space-x-2 px-6 py-3 bg-accent text-white rounded-lg font-heading font-semibold hover:bg-opacity-90 transition-all duration-300"
              >
                <span>Continue</span>
                <Icon name="ArrowRightIcon" size={20} variant="outline" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="ml-auto flex items-center space-x-2 px-8 py-3 bg-accent text-white rounded-lg font-heading font-semibold hover:bg-opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} variant="outline" className="animate-spin" />
                    <span>Scheduling...</span>
                  </>
                ) : (
                  <>
                    <Icon name="CheckIcon" size={20} variant="outline" />
                    <span>Confirm & Schedule</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {benefits.map((benefit, index) => (
            <BenefitCard
              key={index}
              icon={benefit.icon}
              title={benefit.title}
              description={benefit.description}
            />
          ))}
        </div>

        <div className="bg-primary rounded-2xl p-8 md:p-12 text-center">
          <h2 className="text-3xl font-heading font-bold text-primary-foreground mb-4">
            Questions Before Getting Started?
          </h2>
          <p className="text-lg text-primary-foreground/80 mb-6 max-w-2xl mx-auto">
            Our team is available to answer any questions about our investment process, strategies,
            or how we can help achieve your financial goals.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <a
              href="mailto:contact@investprodaily.com"
              className="flex items-center space-x-2 text-primary-foreground hover:text-accent transition-colors duration-300"
            >
              <Icon name="EnvelopeIcon" size={20} variant="outline" />
              <span className="font-medium">contact@investprodaily.com</span>
            </a>
            <a
              href="tel:+15551234567"
              className="flex items-center space-x-2 text-primary-foreground hover:text-accent transition-colors duration-300"
            >
              <Icon name="PhoneIcon" size={20} variant="outline" />
              <span className="font-medium">+1 (555) 123-4567</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}